/**
 * PEP-2.5 Home — locked copy (Phase B final draft).
 * Do not invent claims. EN is a faithful translation of approved FR meaning.
 *
 * Product screenshot registry stays INTERNAL until status === REAL.
 * Never expose internal HS asset IDs or developer capture instructions on public Home.
 */
import {
  ITAAS_INSTITUTION,
  ITAAS_PUBLIC_EMAIL,
  ITAAS_PUBLIC_PHONE,
} from "@itaas/pep-contracts";
import type { PepLocale } from "../i18n/types";

export type ProductCaptureAssetId = "HS-01" | "HS-02" | "HS-03";

export interface ProductCaptureSlotSpec {
  id: ProductCaptureAssetId;
  productLabelFr: string;
  productLabelEn: string;
  /** PLACEHOLDER = asset gap (internal). REAL = verified capture may render publicly. */
  status: "PLACEHOLDER" | "REAL";
  /** Public image URL when REAL — never invent. */
  publicSrc?: string;
}

/**
 * Internal screenshot registry — HS assets remain PENDING.
 * Do not render PLACEHOLDER entries on prospect-facing UI.
 */
export const HOME_PRODUCT_CAPTURES: readonly ProductCaptureSlotSpec[] = [
  {
    id: "HS-01",
    productLabelFr: "ITaaS Edu — Dashboard réel",
    productLabelEn: "ITaaS Edu — real dashboard",
    status: "PLACEHOLDER",
  },
  {
    id: "HS-02",
    productLabelFr: "Billing / Inventory — interface réelle",
    productLabelEn: "Billing / Inventory — real interface",
    status: "PLACEHOLDER",
  },
  {
    id: "HS-03",
    productLabelFr: "MinePortal / RH — interface réelle",
    productLabelEn: "MinePortal / RH — real interface",
    status: "PLACEHOLDER",
  },
] as const;

/** Verified captures only — empty while HS-* are PENDING. */
export function getPublicProductCaptures(): ProductCaptureSlotSpec[] {
  return HOME_PRODUCT_CAPTURES.filter((slot) => slot.status === "REAL" && Boolean(slot.publicSrc));
}

export interface HomeDomain {
  id: string;
  titleFr: string;
  titleEn: string;
  outcomeFr: string;
  outcomeEn: string;
  href: string;
  productHintFr: string;
  productHintEn: string;
  icon: "education" | "hr" | "finance" | "ops" | "mines";
}

export const HOME_DOMAINS: readonly HomeDomain[] = [
  {
    id: "education",
    titleFr: "Éducation",
    titleEn: "Education",
    outcomeFr: "Pilotez scolarité, classes et parcours dans un système métier dédié.",
    outcomeEn: "Run schooling, classes and pathways in a dedicated business system.",
    productHintFr: "ITaaS Edu",
    productHintEn: "ITaaS Edu",
    href: "/products/edu",
    icon: "education",
  },
  {
    id: "hr",
    titleFr: "Capital humain",
    titleEn: "Human capital",
    outcomeFr: "Centralisez les processus RH de l’organisation.",
    outcomeEn: "Centralize your organization’s HR processes.",
    productHintFr: "ITaaS RH",
    productHintEn: "ITaaS RH",
    href: "/products/rh",
    icon: "hr",
  },
  {
    id: "finance",
    titleFr: "Finance",
    titleEn: "Finance",
    outcomeFr: "Facturation et pilotage financier dans le périmètre Suite publié.",
    outcomeEn: "Billing and financial steering within the published Suite scope.",
    productHintFr: "Billing · ERP",
    productHintEn: "Billing · ERP",
    href: "/products/billing",
    icon: "finance",
  },
  {
    id: "ops",
    titleFr: "Opérations",
    titleEn: "Operations",
    outcomeFr: "Stock et opérations quotidiennes sur Inventory.",
    outcomeEn: "Stock and day-to-day operations on Inventory.",
    productHintFr: "Inventory",
    productHintEn: "Inventory",
    href: "/products/inventory",
    icon: "ops",
  },
  {
    id: "mines",
    titleFr: "Industrie & mines",
    titleEn: "Industry & mining",
    outcomeFr: "Suivi de sites et opérations minières via MinePortal.",
    outcomeEn: "Site tracking and mining operations via MinePortal.",
    productHintFr: "MinePortal",
    productHintEn: "MinePortal",
    href: "/products/mineportal",
    icon: "mines",
  },
] as const;

export type HomeSolutionSlug =
  | "edu"
  | "rh"
  | "mineportal"
  | "inventory"
  | "erp"
  | "billing"
  | "hospitol"
  | "security";

export type HomeSolutionAvailability = "flagship" | "limited";

export interface HomeSolutionTeaser {
  slug: HomeSolutionSlug;
  /** Product label (eyebrow on card) */
  labelFr: string;
  labelEn: string;
  /** Domain title under label */
  titleFr: string;
  titleEn: string;
  lineFr: string;
  lineEn: string;
  ctaFr: string;
  ctaEn: string;
  imageSrc: string;
  imageAltFr: string;
  imageAltEn: string;
  icon: import("./HomeIcons").DomainIconName;
  objectPosition: string;
  /** Catalog role for Home presentation — from Portfolio Policy publicRole */
  availability: HomeSolutionAvailability;
  /** Home carousel priority (lower = earlier). Flagships first. */
  homePriority: number;
}

/**
 * Home Solutions carousel — public catalog products only.
 * Order: EDU → RH → MinePortal → other PRIMARY → LIMITED.
 * Editorial photography only — never product screenshots / HS evidence.
 * Canonical product truth remains catalog-index + Portfolio Policy.
 */
export const HOME_SOLUTION_TEASERS: readonly HomeSolutionTeaser[] = [
  {
    slug: "edu",
    labelFr: "ITaaS EDU",
    labelEn: "ITaaS EDU",
    titleFr: "Éducation",
    titleEn: "Education",
    lineFr:
      "Pilotez votre établissement scolaire de la gestion administrative au suivi pédagogique.",
    lineEn:
      "Run your school from administrative management through pedagogical follow-up.",
    ctaFr: "Découvrir EDU",
    ctaEn: "Discover EDU",
    imageSrc: "/assets/home/home-solution-edu.webp",
    imageAltFr:
      "Illustration éditoriale : enseignant et élèves dans une classe en RDC. Ce n’est pas une capture produit.",
    imageAltEn:
      "Editorial illustration: teacher and students in a classroom in the DRC. Not a product screenshot.",
    icon: "education",
    objectPosition: "50% 28%",
    availability: "flagship",
    homePriority: 1,
  },
  {
    slug: "rh",
    labelFr: "ITaaS RH",
    labelEn: "ITaaS RH",
    titleFr: "Capital humain",
    titleEn: "Human capital",
    lineFr: "Centralisez la gestion de vos collaborateurs, compétences et processus RH.",
    lineEn: "Centralize employee management, skills and HR processes.",
    ctaFr: "Découvrir RH",
    ctaEn: "Discover RH",
    imageSrc: "/assets/home/home-solution-rh.webp",
    imageAltFr:
      "Illustration éditoriale : professionnels RH dans un environnement de bureau africain. Ce n’est pas une capture produit.",
    imageAltEn:
      "Editorial illustration: HR professionals in an African office setting. Not a product screenshot.",
    icon: "hr",
    objectPosition: "50% 22%",
    availability: "flagship",
    homePriority: 2,
  },
  {
    slug: "mineportal",
    labelFr: "MINEPORTAL",
    labelEn: "MINEPORTAL",
    titleFr: "Industrie & mines",
    titleEn: "Industry & mining",
    lineFr: "Pilotez vos sites et opérations minières depuis une plateforme intégrée et sécurisée.",
    lineEn: "Run your mining sites and operations from an integrated, secure platform.",
    ctaFr: "Découvrir MinePortal",
    ctaEn: "Discover MinePortal",
    imageSrc: "/assets/home/home-solution-mineportal.webp",
    imageAltFr:
      "Illustration éditoriale : supervision d’opérations minières en RDC. Ce n’est pas une capture produit.",
    imageAltEn:
      "Editorial illustration: mining operations supervision in the DRC. Not a product screenshot.",
    icon: "mines",
    objectPosition: "35% 30%",
    availability: "flagship",
    homePriority: 3,
  },
  {
    slug: "inventory",
    labelFr: "ITaaS Inventory",
    labelEn: "ITaaS Inventory",
    titleFr: "Opérations",
    titleEn: "Operations",
    lineFr: "Centralisez catalogue, entrepôts et mouvements de stock sur une plateforme commune.",
    lineEn: "Centralize catalog, warehouses and stock movements on one shared platform.",
    ctaFr: "Découvrir Inventory",
    ctaEn: "Discover Inventory",
    imageSrc: "/assets/home/home-solution-inventory.webp",
    imageAltFr:
      "Illustration éditoriale : opérations d’entrepôt et de stock en RDC. Ce n’est pas une capture produit.",
    imageAltEn:
      "Editorial illustration: warehouse and stock operations in the DRC. Not a product screenshot.",
    icon: "ops",
    objectPosition: "45% 30%",
    availability: "flagship",
    homePriority: 4,
  },
  {
    slug: "erp",
    labelFr: "ITaaS ERP",
    labelEn: "ITaaS ERP",
    titleFr: "ERP",
    titleEn: "ERP",
    lineFr: "Pilotez organisation, sites et workflows sur un ERP modulaire multi-tenant.",
    lineEn: "Run organization, sites and workflows on a modular multi-tenant ERP.",
    ctaFr: "Découvrir ERP",
    ctaEn: "Discover ERP",
    imageSrc: "/assets/home/home-solution-erp.webp",
    imageAltFr:
      "Illustration éditoriale : collaboration opérationnelle en entreprise africaine. Ce n’est pas une capture produit.",
    imageAltEn:
      "Editorial illustration: operations collaboration in an African enterprise. Not a product screenshot.",
    icon: "erp",
    objectPosition: "50% 25%",
    availability: "flagship",
    homePriority: 5,
  },
  {
    slug: "billing",
    labelFr: "ITaaS Invoice",
    labelEn: "ITaaS Invoice",
    titleFr: "Finance",
    titleEn: "Finance",
    lineFr: "Émettez factures, devis et paiements dans le module Invoice de la Suite.",
    lineEn: "Issue invoices, quotes and payments with the Suite Invoice module.",
    ctaFr: "Découvrir Invoice",
    ctaEn: "Discover Invoice",
    imageSrc: "/assets/home/home-solution-billing.webp",
    imageAltFr:
      "Illustration éditoriale : professionnel finance en contexte africain. Ce n’est pas une capture produit.",
    imageAltEn:
      "Editorial illustration: finance professional in an African business context. Not a product screenshot.",
    icon: "finance",
    objectPosition: "50% 28%",
    availability: "flagship",
    homePriority: 6,
  },
  {
    slug: "hospitol",
    labelFr: "ITaaS Hospitol",
    labelEn: "ITaaS Hospitol",
    titleFr: "Santé",
    titleEn: "Healthcare",
    lineFr: "Reliez accueil, soins et caisse autour d’un dossier patient unique.",
    lineEn: "Connect reception, care and billing around one continuous patient record.",
    ctaFr: "Découvrir Hospitol",
    ctaEn: "Discover Hospitol",
    imageSrc: "/assets/home/home-solution-hospitol.webp",
    imageAltFr:
      "Illustration éditoriale : accueil et administration hospitalière en RDC. Ce n’est pas une capture produit.",
    imageAltEn:
      "Editorial illustration: hospital reception and administration in the DRC. Not a product screenshot.",
    icon: "health",
    objectPosition: "45% 25%",
    availability: "limited",
    homePriority: 7,
  },
  {
    slug: "security",
    labelFr: "ITaaS Security",
    labelEn: "ITaaS Security",
    titleFr: "Sécurité",
    titleEn: "Security",
    lineFr: "Pilotez sites, agents et conformité terrain pour sociétés de sécurité.",
    lineEn: "Run sites, agents and field compliance for security companies.",
    ctaFr: "Découvrir Security",
    ctaEn: "Discover Security",
    imageSrc: "/assets/home/home-solution-security.webp",
    imageAltFr:
      "Illustration éditoriale : supervision de site de sécurité privée en RDC. Ce n’est pas une capture produit.",
    imageAltEn:
      "Editorial illustration: private security site supervision in the DRC. Not a product screenshot.",
    icon: "security",
    objectPosition: "40% 28%",
    availability: "limited",
    homePriority: 8,
  },
] as const;

/** Ordered list for Home carousel (data-driven; add eligible products here). */
export function getHomeCarouselSolutions(): readonly HomeSolutionTeaser[] {
  return [...HOME_SOLUTION_TEASERS].sort((a, b) => a.homePriority - b.homePriority);
}

export const HOME_DIFF_POINTS = [
  {
    id: "suite",
    fr: "Architecture de suite intégrée",
    en: "Integrated suite architecture",
  },
  {
    id: "local",
    fr: "Éditeur et accompagnement ancrés à Lubumbashi",
    en: "Publisher and support rooted in Lubumbashi",
  },
  {
    id: "modular",
    fr: "Adoption modulaire, produit par produit",
    en: "Modular adoption, product by product",
  },
] as const;

/** Publisher NAP for Home trust band — address/email/phone from contracts (Kampemba). No legal ID promotion. */
export const HOME_PUBLISHER = {
  brandName: ITAAS_INSTITUTION.brandName,
  roleFr: "éditeur logiciel",
  roleEn: "software publisher",
  addressLines: ITAAS_INSTITUTION.address.displayLines,
  email: ITAAS_PUBLIC_EMAIL,
  phone: ITAAS_PUBLIC_PHONE,
  localityLineFr: `${ITAAS_INSTITUTION.address.addressLocality} — RDC`,
  localityLineEn: `${ITAAS_INSTITUTION.address.addressLocality} — DRC`,
} as const;

export function homeCopy(locale: PepLocale) {
  const en = locale === "en";
  return {
    seoTitle: en
      ? "ITaaS Suite | Business software platform in the DRC"
      : "ITaaS Suite | Plateforme logicielle métier en RDC",
    seoDescription: en
      ? "Integrated business software for education, human resources, finance, inventory, operations and industry — published and supported by ITaaS RDC in Lubumbashi."
      : "Plateforme logicielle métier intégrée pour l’éducation, les ressources humaines, la finance, les stocks, les opérations et l’industrie — éditée et accompagnée par ITaaS RDC à Lubumbashi.",
    eyebrow: en ? "ITaaS Suite · by ITaaS RDC" : "ITaaS Suite · par ITaaS RDC",
    h1Lead: en ? "Run your entire organization from" : "Pilotez toute votre organisation depuis",
    h1Emphasis: en ? "one unique platform." : "une plateforme unique.",
    /** Full H1 string for SEO / accessibility fallbacks */
    h1: en
      ? "Run your entire organization from one unique platform."
      : "Pilotez toute votre organisation depuis une plateforme unique.",
    support: en
      ? "Education, human resources, finance, inventory and operations: centralize your activities, teams and data in one environment — designed and supported in the DRC."
      : "Éducation, ressources humaines, finance, stocks et opérations : centralisez vos activités, vos équipes et vos données dans un même environnement, conçu et accompagné en RDC.",
    primaryCta: en ? "Request a demonstration" : "Demander une démonstration",
    secondaryCta: en ? "Discover the solutions" : "Découvrir les solutions",
    trustChip: en
      ? "Congolese software publisher · Lubumbashi, DRC"
      : "Éditeur logiciel congolais · Lubumbashi, RDC",
    heroImageSrc: "/assets/home/hero-itaas-connected-organisation.webp?v=20260728-fusion2",
    /** Owner-approved Hero asset (2026-07-28). Legacy retained for rollback only. */
    heroImageSrcLegacy: "/assets/home/hero-ecosystem.png",
    heroVisualAlt: en
      ? "Conceptual illustration: ITaaS Suite as a connected platform linking education, human resources, finance, inventory and industry operations. Editorial imagery — not a product screenshot and not customer proof."
      : "Illustration conceptuelle : ITaaS Suite comme plateforme connectée reliant éducation, ressources humaines, finance, stocks et industrie & opérations. Visuel éditorial — ce n’est pas une capture produit ni une preuve client.",
    heroDomainsLabel: en ? "Business domains around ITaaS Suite" : "Univers métier autour d’ITaaS Suite",
    heroDomains: en
      ? ([
          { id: "education", label: "Education" },
          { id: "hr", label: "Human resources" },
          { id: "finance", label: "Finance" },
          { id: "inventory", label: "Inventory & stock" },
          { id: "industry", label: "Industry & operations" },
        ] as const)
      : ([
          { id: "education", label: "Éducation" },
          { id: "hr", label: "Ressources humaines" },
          { id: "finance", label: "Finance" },
          { id: "inventory", label: "Stocks & inventaire" },
          { id: "industry", label: "Industrie & opérations" },
        ] as const),
    valueEyebrow: en ? "All-in-one · Secure · Scalable" : "Tout-en-un · Sécurisé · Évolutif",
    valueH2: en
      ? "A complete ecosystem for every line of business"
      : "Un écosystème complet pour tous vos métiers",
    valueIntro: en
      ? "ITaaS Suite brings together integrated, interconnected business applications to simplify day-to-day work and accelerate performance."
      : "ITaaS Suite réunit des applications métier intégrées et interconnectées pour simplifier votre quotidien et accélérer vos performances.",
    valuePillars: en
      ? ([
          {
            id: "security",
            title: "Security & compliance",
            body: "Secured data, hosted with DRC-oriented operations, aligned with norms and good practices.",
          },
          {
            id: "data",
            title: "Unified data",
            body: "One source of truth for reliable decisions and real-time steering.",
          },
          {
            id: "scale",
            title: "Scalable & flexible",
            body: "A modular platform that adapts to your needs and grows with you.",
          },
        ] as const)
      : ([
          {
            id: "security",
            title: "Sécurité & conformité",
            body: "Données sécurisées, hébergées en RDC, conformes aux normes et bonnes pratiques.",
          },
          {
            id: "data",
            title: "Données unifiées",
            body: "Une seule source de vérité pour des décisions fiables et un pilotage en temps réel.",
          },
          {
            id: "scale",
            title: "Évolutif & flexible",
            body: "Une plateforme modulaire qui s’adapte à vos besoins et grandit avec vous.",
          },
        ] as const),
    proofH2: en
      ? "One ecosystem. Several lines of business. One platform."
      : "Un écosystème. Plusieurs métiers. Une plateforme.",
    proofBody: en
      ? "Business applications on a shared platform — designed and operated by ITaaS RDC for professional organizations."
      : "Des applications métier sur une plateforme commune — conçues et opérées par ITaaS RDC pour les organisations professionnelles.",
    domainsH2: en ? "Business domains" : "Domaines d’activité",
    healthNoteLabel: en ? "Also available with limited readiness" : "Également disponible — disponibilité limitée",
    healthNoteProduct: "Hospitol",
    healthNoteHref: "/products/hospitol",
    solutionsH2: en ? "Solutions" : "Solutions",
    solutionsCatalog: en ? "Discover all solutions" : "Découvrir toutes les solutions",
    solutionsLimitedLabel: en ? "Limited readiness" : "Disponibilité limitée",
    solutionsLimitedBody: en
      ? "Hospitol and Security are published with limited readiness — not equivalent to flagship offers."
      : "Hospitol et Security sont publiés en disponibilité limitée — pas au même niveau que les offres phares.",
    learnMore: en ? "Learn more" : "En savoir plus",
    solutionsEditorialNote: en
      ? "Editorial métier imagery — not product screenshots."
      : "Visuels éditoriaux métier — ce ne sont pas des captures produit.",
    carouselPrev: en ? "Previous solutions" : "Solutions précédentes",
    carouselNext: en ? "Next solutions" : "Solutions suivantes",
    platformH2: en ? "A suite, not an isolated collection" : "Une suite, pas une collection isolée",
    platformBody: en
      ? "Business applications, shared services, identity, data, integration and security — on one common platform."
      : "Applications métier, services partagés, identité, données, intégration et sécurité — sur une plateforme commune.",
    platformBand1: en ? "Business applications" : "Applications métier",
    platformBand2: en ? "Shared ITaaS Suite services" : "Services partagés ITaaS Suite",
    platformBand3: en
      ? "Identity · Data · Integration · Security"
      : "Identité · Données · Intégration · Sécurité",
    /** Shown only when REAL product captures exist (navy proof band). */
    productProofH2: en
      ? "Real applications. Built for your lines of business."
      : "Des applications réelles. Conçues pour vos métiers.",
    productProofBody: en
      ? "Authenticated product views from the published ITaaS Suite."
      : "Vues produit authentifiées issues de la Suite ITaaS publiée.",
    diffH2: en ? "Why decision-makers look at ITaaS" : "Pourquoi les décideurs regardent ITaaS",
    trustH2: en ? "The company behind the suite" : "L’entreprise derrière la suite",
    aboutLink: en ? "About ITaaS RDC" : "À propos d’ITaaS RDC",
    actionH2: en
      ? "Let’s build the solution for your organization."
      : "Construisons la solution adaptée à votre organisation.",
    actionSupport: en
      ? "A demonstration with ITaaS RDC — response within 48 business hours."
      : "Une démonstration avec ITaaS RDC — réponse sous 48 heures ouvrées.",
    actionSecondary: en ? "Contact us" : "Nous contacter",
    ecosystemVisualAlt: en
      ? "Conceptual illustration of the ITaaS Suite ecosystem — business applications on a shared platform. Not a product screenshot."
      : "Illustration conceptuelle de l’écosystème ITaaS Suite — applications métier sur une plateforme commune. Ce n’est pas une capture produit.",
    demoHref: "/contact?type=demo",
    productsHref: "/products",
    contactHref: "/contact",
    aboutHref: "/about",
  };
}
